import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { PAGE_BG, BTN_PRIMARY, INPUT, ALERT_ERROR } from "../constants/ui";
import { API_ENDPOINTS } from "../constants/apiEndpoints";

type SignupResponse = {
  message: string;
  user: {
    id: number;
    name: string;
    username: string;
    email: string;
    role?: string; // ✅ 백엔드 UserSerializer에 role 있음
  };
    access: string;
    refresh: string;
};

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [role, setRole] = useState<"student" | "parent">("student");
  const [name, setName] = useState("");
  const [username, setUsername] = useState(""); // 아이디
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");

  const [agreedTerms, setAgreedTerms] = useState(false);
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 화면 표시용(디버그): api 인스턴스의 baseURL 표시
  const apiBase = useMemo(() => api.defaults.baseURL ?? "", []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim() || !username.trim() || !email.trim() || !password || !password2) {
      setError("모든 항목을 입력해주세요.");
      return;
    }
    if (!/^[A-Za-z0-9_]+$/.test(username.trim())) {
      setError("아이디는 영문, 숫자, 밑줄(_)만 사용할 수 있습니다.");
      return;
    }
    if (role === "student" && !phone.trim()) {
      setError("학생 가입 시 연락처는 필수입니다. 선생님이 등록한 연락처와 동일하게 입력해주세요.");
      return;
    }
    if (password !== password2) {
      setError("비밀번호 확인이 일치하지 않습니다.");
      return;
    }
    if (!agreedTerms || !agreedPrivacy) {
      setError("이용약관 및 개인정보처리방침에 동의해주세요.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // ✅ axiosInstance의 baseURL이 `${REACT_APP_API_BASE_URL}/api` 이므로
      // 여기서는 "/auth/signup/" 만 호출하면 됨
      const res = await api.post<SignupResponse>(API_ENDPOINTS.AUTH.SIGNUP, {
        name: name.trim(),
        username: username.trim(),
        email: email.trim(),
        phone: phone.trim(),
        role,
        password,
        password2,
      });

      const { user, access, refresh } = res.data;

      // ✅ user 정보 별도 저장 (role 포함) → 대시보드/헤더 등에서 활용
      localStorage.setItem("auth_user", JSON.stringify(user));

      // ✅ 토큰 저장(자동 로그인) → AuthContext + axiosInstance와 연동
      login(access, refresh);

      // role별 첫 화면 분기
      navigate(user.role === "parent" ? "/parent" : "/teacher", { replace: true });
    } catch (err: any) {
      console.error(err);
      const data = err?.response?.data;

      // DRF ValidationError: {field: ["msg"]} 형태 대응
      if (data && typeof data === "object") {
        const firstKey = Object.keys(data)[0];
        const msg = Array.isArray(data[firstKey])
          ? data[firstKey][0]
          : String(data[firstKey]);
        setError(`❌ 회원가입 실패: ${msg}`);
      } else {
        setError("❌ 회원가입 실패: 서버 오류가 발생했습니다.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${PAGE_BG} flex items-center justify-center px-4 py-10`}>
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ring-1 ring-slate-200/60">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 overflow-hidden rounded-2xl bg-slate-900/5 ring-1 ring-slate-200">
            <img
              src="/logo.png"
              alt="새로운 연구소 로고"
              className="h-full w-full object-cover"
            />
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-tight">회원가입</h1>
            <p className="text-xs text-slate-500">새로운 연구소 계정 생성</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 grid gap-3">
          {/* 역할 선택 */}
          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">가입 유형</label>
            <div className="grid grid-cols-2 gap-2">
              {(["student", "parent"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setRole(r)}
                  className={`rounded-xl border py-2.5 text-sm font-semibold transition-colors ${
                    role === r
                      ? "border-[#57c4c6] bg-teal-50 text-[#2aa7aa]"
                      : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  {r === "student" ? "🎓 학생" : "👨‍👩‍👧 학부모"}
                </button>
              ))}
            </div>
            {role === "parent" && (
              <p className="text-xs text-teal-600 mt-1">
                💡 자녀의 학부모 연락처와 동일한 전화번호로 가입하면 자녀 정보와 자동 연결됩니다.
              </p>
            )}
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">이름</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              className={INPUT}
              placeholder="홍길동"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">
              아이디
            </label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/[^A-Za-z0-9_]/g, ''))}
              disabled={loading}
              className={INPUT}
              placeholder="영문, 숫자, 밑줄(_)만 입력 가능"
              autoComplete="username"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">
              이메일
            </label>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              className={INPUT}
              placeholder="email@example.com"
              autoComplete="email"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">
              연락처{role === "student" ? " (필수)" : " (선택)"}
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              disabled={loading}
              className={INPUT}
              placeholder="010-1234-5678"
              autoComplete="tel"
            />
            <p className="text-xs text-slate-500 mt-1">
              {role === "parent"
                ? "💡 자녀의 학부모 연락처와 동일한 번호를 입력하면 자녀 정보와 자동 연결됩니다."
                : "⚠️ 선생님이 등록한 연락처와 동일하게 입력해야 학생 정보와 연결됩니다."}
            </p>
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">
              비밀번호
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              className={INPUT}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>

          <div className="grid gap-1">
            <label className="text-xs font-semibold text-slate-700">
              비밀번호 확인
            </label>
            <input
              type="password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              disabled={loading}
              className={INPUT}
              placeholder="••••••••"
              autoComplete="new-password"
            />
          </div>

          {/* 약관 동의 */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-2.5">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedTerms}
                onChange={(e) => setAgreedTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#57c4c6]"
              />
              <span className="text-xs text-slate-600">
                (필수){" "}
                <Link to="/terms" target="_blank" className="text-[#2aa7aa] underline">
                  서비스 이용약관
                </Link>
                에 동의합니다.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreedPrivacy}
                onChange={(e) => setAgreedPrivacy(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[#57c4c6]"
              />
              <span className="text-xs text-slate-600">
                (필수){" "}
                <Link to="/privacy" target="_blank" className="text-[#2aa7aa] underline">
                  개인정보처리방침
                </Link>
                에 동의합니다.
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !agreedTerms || !agreedPrivacy}
            className={`mt-2 w-full ${BTN_PRIMARY}`}
          >
            {loading ? "가입 중..." : "회원가입"}
          </button>

          {error && (
            <div className={ALERT_ERROR}>{error}</div>
          )}
        </form>

        <div className="mt-5 flex items-center justify-between text-sm">
          <Link to="/" className="text-slate-500 hover:underline">
            ← 홈으로
          </Link>
          <Link
            to="/login"
            className="font-semibold text-[#2aa7aa] hover:underline"
          >
            로그인
          </Link>
        </div>

        <div className="mt-4 text-[11px] text-slate-400">
          API: <span className="font-mono">{apiBase}</span>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
